import React, { useCallback, useMemo, useState } from "react";
import { Skeleton } from "antd";
import Button from "@dtci/spellbook/Button";
import { usePPTState, usePPTActions } from "tap/store/ppt/usePPT";
import {
  completePromotionStage,
  sendBackPromotionStage,
  fetchPromotion,
  fetchPromotionDiscounts,
} from "ppt/api";
import { usePPTToasts } from "tap/pages/PPT/hooks/usePPTToasts";
import { Stepper } from "./Stepper";
import { ActionBar } from "./ActionBar";
import { AdvanceModal } from "./AdvanceModal";
import { SendBackModal } from "./SendBackModal";
import styles from "./pptStatusLifecycle.module.scss";
import {
  computeRollup,
  currentStageHealth,
  discountsAtSeverity,
} from "./lifecycleRollup";
import { gatePolicyFor, evaluateGate } from "./gatePolicies";

/**
 * Lifecycle widget for the promotion detail page.
 *
 * Layout (sub-divided):
 *   - Header     : title (left) · legend (right of title)
 *   - Body left  : stepper + Complete Stage CTA + blocked reason if any
 *   - Body right : 4 stat tiles (quick filters for the discount grid) +
 *                  activity line (last stage transition / lifecycle started)
 *
 * Filter dispatch: clicking a stat tile dispatches `setLifecycleFilter` to
 * Redux; the discount grid below subscribes to that state and applies the
 * filter via AG Grid's external-filter callbacks.
 */
export const PPTStatusLifecycle = () => {
  const {
    promotionStages: stages,
    promotionDetail,
    promotionDetailLoading,
    promotionStagesLoading,
    discountDetails,
  } = usePPTState();
  const {
    updatePromotionDetail,
    updateDiscountDetailsData,
    setPromotionStagesLoading,
  } = usePPTActions();
  const { showErrorToast } = usePPTToasts();

  const [modalMode, setModalMode] = useState(null); // "clean" | "warnings" | "blocked" | null
  const [sendBackOpen, setSendBackOpen] = useState(false);

  const promotionId = promotionDetail?.id;

  const currentStageIndex = useMemo(() => {
    const currentStageId = promotionDetail?.currentStage?.id;
    if (!currentStageId || !stages?.length) return -1;
    return stages.findIndex((s) => s.id === currentStageId);
  }, [stages, promotionDetail?.currentStage?.id]);

  const currentStage =
    currentStageIndex >= 0 ? stages[currentStageIndex] : null;

  const isDone =
    Array.isArray(stages) &&
    stages.length > 0 &&
    !promotionDetailLoading &&
    !currentStage;

  const rollup = useMemo(
    () =>
      computeRollup(discountDetails, promotionDetail?.discountCountByStatus),
    [discountDetails, promotionDetail?.discountCountByStatus],
  );

  const health = useMemo(() => currentStageHealth(rollup), [rollup]);

  const gatePolicy = currentStage ? gatePolicyFor(currentStage.id) : null;
  const gateEvaluation = useMemo(
    () => evaluateGate(gatePolicy, rollup.activeErrors, rollup.activeWarnings),
    [gatePolicy, rollup.activeErrors, rollup.activeWarnings],
  );

  const errorDiscounts = useMemo(
    () => discountsAtSeverity(discountDetails, "ERROR"),
    [discountDetails],
  );
  const warningDiscounts = useMemo(
    () => discountsAtSeverity(discountDetails, "WARNING"),
    [discountDetails],
  );

  // Pull a useful message off an axios error: prefer the backend's RFC 7807
  // problem-detail `detail` (e.g. "Send-back target portal_upload must be
  // before the current stage"), fall back to whatever readable text we can
  // find. Keeps the toast informative instead of "please try again later."
  const errorDetail = (err) =>
    err?.response?.data?.detail ||
    err?.response?.data?.message ||
    err?.message ||
    null;

  // Refetch promotion and discounts after a short delay to pick up any auto-accepted
  // pending changes that may have been applied by the backend after the stage transition.
  const refreshPromotionAfterStageChange = useCallback(() => {
    setTimeout(async () => {
      try {
        const [{ data: promotionData }, discountsData] = await Promise.all([
          fetchPromotion(promotionId),
          fetchPromotionDiscounts(promotionId),
        ]);
        updatePromotionDetail(promotionData);
        updateDiscountDetailsData(discountsData);
      } catch (refreshErr) {
        console.error(
          "Failed to refetch promotion after stage change:",
          refreshErr,
        );
        showErrorToast("Error refreshing Promotion. Please reload the page.");
      }
    }, 500);
  }, [
    promotionId,
    updatePromotionDetail,
    updateDiscountDetailsData,
    showErrorToast,
  ]);

  const advance = useCallback(async () => {
    if (!promotionId || !currentStage) return;
    try {
      setPromotionStagesLoading(true);
      const { data } = await completePromotionStage(
        promotionId,
        currentStage.id,
      );
      updatePromotionDetail(data);
      setModalMode(null);
      refreshPromotionAfterStageChange();
    } catch (err) {
      console.error("Error advancing promotion stage:", err, err?.response);
      const detail = errorDetail(err);
      showErrorToast(
        detail
          ? `Could not advance: ${detail}`
          : "Error updating Promotion stage. Please try again later.",
      );
    } finally {
      setPromotionStagesLoading(false);
    }
  }, [
    promotionId,
    currentStage,
    setPromotionStagesLoading,
    updatePromotionDetail,
    showErrorToast,
  ]);

  const sendBack = useCallback(
    async ({ stageId, reason }) => {
      if (!promotionId) return;
      try {
        setPromotionStagesLoading(true);
        const { data } = await sendBackPromotionStage(
          promotionId,
          stageId,
          reason,
        );
        updatePromotionDetail(data);
        setSendBackOpen(false);
        refreshPromotionAfterStageChange();
      } catch (err) {
        console.error(
          "Error sending back promotion stage:",
          err,
          err?.response,
        );
        const detail = errorDetail(err);
        showErrorToast(
          detail
            ? `Could not send back: ${detail}`
            : "Error sending back Promotion stage. Please try again later.",
        );
      } finally {
        setPromotionStagesLoading(false);
      }
    },
    [
      promotionId,
      setPromotionStagesLoading,
      updatePromotionDetail,
      showErrorToast,
    ],
  );

  // Always open the modal — even on a clean gate the user gets a confirmation
  // step. Mode mirrors the gate evaluation: "clean" / "warnings" / "blocked".
  const handleCompleteClick = useCallback(() => {
    setModalMode(gateEvaluation.mode);
  }, [gateEvaluation.mode]);

  const nextStageName = useMemo(() => {
    if (currentStageIndex < 0) return null;
    return stages[currentStageIndex + 1]?.name ?? null;
  }, [stages, currentStageIndex]);

  if (!stages || stages.length === 0) return null;

  return (
    <Skeleton
      loading={promotionDetailLoading}
      active
      title={false}
      paragraph={{ rows: 4, width: "100%" }}
    >
      <div className={styles.body}>
        {/* Left zone — the lifecycle: stepper + next-action CTA. */}
        <div className={styles.bodyLifecycle}>
          <Stepper
            stages={stages}
            currentStageIndex={currentStageIndex}
            currentHealth={health}
            isDone={isDone}
          />
          {(isDone || currentStage) && (
            <div className={styles.actionSection}>
              <div className={styles.actionRow}>
                {(isDone || currentStageIndex > 1) && (
                  <Button
                    type="button"
                    styleType="standard"
                    colorType="base"
                    onClick={() => setSendBackOpen(true)}
                    disabled={promotionStagesLoading}
                    iconType="fa-arrow-left"
                    iconPlacement="before"
                  >
                    Send back
                  </Button>
                )}
                {!isDone && (
                  <ActionBar
                    gateEvaluation={gateEvaluation}
                    onComplete={handleCompleteClick}
                    isLoading={promotionStagesLoading}
                    currentStageId={currentStage?.id}
                  />
                )}
              </div>
              {isDone ? (
                <p className={styles.subtitle}>
                  <span className={styles.subtitleStage}>
                    All stages complete.
                  </span>
                </p>
              ) : (
                !gateEvaluation.allowed &&
                gateEvaluation.reason && (
                  <p className={`${styles.subtitle} ${styles.subtitleBlocked}`}>
                    {gateEvaluation.reason}
                  </p>
                )
              )}
            </div>
          )}
        </div>
      </div>

      <AdvanceModal
        open={modalMode !== null}
        mode={modalMode || "clean"}
        currentStageName={currentStage?.name || ""}
        nextStageName={nextStageName}
        errorDiscounts={errorDiscounts}
        warningDiscounts={warningDiscounts}
        onCancel={() => setModalMode(null)}
        onConfirm={advance}
        isConfirming={promotionStagesLoading}
      />

      <SendBackModal
        open={sendBackOpen}
        stages={stages}
        currentStageIndex={currentStageIndex}
        onCancel={() => setSendBackOpen(false)}
        onConfirm={sendBack}
        isConfirming={promotionStagesLoading}
      />
    </Skeleton>
  );
};
